import { useState, useEffect } from 'react';
import Sidebar from '../Components/Sidebar';
import { Eye, EyeOff, Plus, Search, User, Key, RefreshCw, Trash2, Lock, Unlock, Shield, AlertCircle, Check, X, Mail, Calendar, MonitorUp } from 'lucide-react';
import apiClient from '../Intercepter/APiClient';

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [filteredUsers, setFilteredUsers] = useState(users);
  
  const [increaseQuotaModal, setIncreaseQuotaModal] = useState(false);
  const [addUserModal, setAddUserModal] = useState(false);
  const [apiKeyModal, setApiKeyModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [resetKeyModal, setResetKeyModal] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [increaseAmount, setIncreaseAmount] = useState('');
  const [newUser, setNewUser] = useState({ regNo: '', name: '', email: '', apiLimit: 50, premiumTaken: false , RTPStatus: false });
  const [showApiKey, setShowApiKey] = useState(false);
  
  const itemsPerPage = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage) || 1;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [suspendReason, setSuspendReason] = useState('');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/users');
      
      if (response.data.success) {
        const formattedUsers = response.data.users.map(user => ({
          id: user.id,
          regNo: user.registration_number,
          name: user.name,
          email: user.email,
          apiLimit: user.api_limit,
          apiUsed: user.api_used,
          apiKey: null,
          // --- FIX: Map the premium status from the API response ---
          premiumStatus: user.premium_status, 
           RTPStatus: user.RTP_status, // Fixed: use user object
          passkey: user.passkey,
          status: user.status, // active, suspended, deleted
          createdAt: user.created_at,
          lastUsed: user.last_api_usage
        }));
        
        setUsers(formattedUsers);
        setError(null);
      } else {
        setError('Failed to fetch users');
      }
    } catch (err) {
      setError('Error connecting to server');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const result = search
      ? users.filter(u => 
          u.regNo?.toLowerCase().includes(search.toLowerCase()) ||
          u.name?.toLowerCase().includes(search.toLowerCase()) ||
          u.email?.toLowerCase().includes(search.toLowerCase())
        )
      : users;
    setFilteredUsers(result);
    setCurrentPage(1);
  }, [search, users]);

  const openIncreaseQuotaModal = (user) => {
    setSelectedUser(user);
    setIncreaseAmount('');
    setIncreaseQuotaModal(true);
  };

  const openApiKeyModal = (user) => {
    setSelectedUser(user);
    setShowApiKey(false);
    setApiKeyModal(true);
  };

  const openSuspendModal = (user) => {
    setSelectedUser(user);
    setSuspendReason('');
    setSuspendModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setDeleteModal(true);
  };

  const openResetKeyModal = (user) => {
    setSelectedUser(user);
    setResetKeyModal(true);
  };

  const handleIncreaseQuota = async () => {
    const amount = parseInt(increaseAmount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid positive number');
      return;
    }
    
    try {
      const response = await apiClient.post('/api/users/increase-quota', {
        user_id: selectedUser.id,
        increase_amount: amount
      });
      
      if (response.data.success) {
        setUsers(users.map(u => (
          u.id === selectedUser.id
            ? { ...u, apiLimit: u.apiLimit + amount }
            : u
        )));
        setIncreaseQuotaModal(false);
        alert('Quota increased successfully');
      } else {
        alert('Failed to increase quota: ' + response.data.message);
      }
    } catch (err) {
      alert('Error updating quota');
      console.error('Error updating quota:', err);
    }
  };

  const handleSuspendUser = async () => {
    try {
      const response = await apiClient.post('/api/v3/users/suspend', {
        user_id: selectedUser.id,
        reason: suspendReason
      });
      
      if (response.data.success) {
        setUsers(users.map(u => (
          u.id === selectedUser.id
            ? { ...u, status: 'suspended' }
            : u
        )));
        setSuspendModal(false);
        alert('User suspended successfully');
      } else {
        alert('Failed to suspend user: ' + response.data.message);
      }
    } catch (err) {
      alert('Error suspending user');
      console.error('Error suspending user:', err);
    }
  };

  const handleUnsuspendUser = async (userId) => {
    try {
      const response = await apiClient.post('/api/v3/users/unsuspend', {
        user_id: userId
      });
      
      if (response.data.success) {
        setUsers(users.map(u => (
          u.id === userId
            ? { ...u, status: 'active' }
            : u
        )));
        alert('User reactivated successfully');
      } else {
        alert('Failed to unsuspend user: ' + response.data.message);
      }
    } catch (err) {
      alert('Error reactivating user');
      console.error('Error reactivating user:', err);
    }
  };

  const handleDeleteUser = async () => {
    try {
      const response = await apiClient.delete(`/api/v3/users/${selectedUser.id}`);
      
      if (response.data.success) {
        setUsers(users.filter(u => u.id !== selectedUser.id));
        setDeleteModal(false);
        alert('User deleted successfully');
      } else {
        alert('Failed to delete user: ' + response.data.message);
      }
    } catch (err) {
      alert('Error deleting user');
      console.error('Error deleting user:', err);
    }
  };

  const handleResetApiKey = async () => {
    try {
      const response = await apiClient.post('/api/v3/users/reset-api-key', {
        user_id: selectedUser.id
      });
      
      if (response.data.success) {
        setUsers(users.map(u => (
          u.id === selectedUser.id
            ? { ...u, apiKey: response.data.new_api_key }
            : u
        )));
        setNewApiKey(response.data.new_api_key);
        setResetKeyModal(false);
        alert('API key reset successfully');
      } else {
        alert('Failed to reset API key: ' + response.data.message);
      }
    } catch (err) {
      alert('Error resetting API key');
      console.error('Error resetting API key:', err);
    }
  };

  const handleAddUser = async () => {
    if (!newUser.name || !newUser.regNo || !newUser.email) {
      alert('Name, Registration Number, and Email are required');
      return;
    }
    
    if (users.some(u => u.regNo === newUser.regNo)) {
      alert('Registration Number already exists');
      return;
    }
    
    try {
      const response = await apiClient.post('/api/users/create', {
        name: newUser.name,
        reg_no: newUser.regNo,
        email: newUser.email,
        api_limit: newUser.apiLimit,
        premium_taken: newUser.premiumTaken || false,
        RTP_status: newUser.RTPStatus || 'inactive'
      });
      
      if (response.data.success) {
        // --- FIX: Use response.data properties, NOT newUser properties ---
        const userToAdd = { 
          id: response.data.user_id,
          regNo: newUser.regNo, 
          name: newUser.name,
          email: newUser.email,
          apiLimit: newUser.apiLimit,
          apiUsed: 0,
          apiKey: response.data.api_key, // Fixed: use response.data
          status: response.data.status, // Fixed: use response.data
          premiumStatus: response.data.premium_status, // Fixed: use response.data
          RTPStatus: response.data.RTP_status, // Fixed: use response.data
          passkey: response.data.passkey, // Fixed: use response.data
          createdAt: new Date().toISOString(), 
          lastUsed: null
        };
        
        setUsers(prev => [...prev, userToAdd]);
        setSelectedUser(userToAdd);
        setNewApiKey(response.data.api_key);
        setAddUserModal(false);
        setApiKeyModal(true);
      } else {
        alert('Failed to add user: ' + response.data.message);
      }
    } catch (err) {
      alert('Error adding user');
      console.error('Error adding user:', err);
    }
  };

  const resetAddUserForm = () => {
    setNewUser({ regNo: '', name: '', email: '', apiLimit: 50, premiumTaken: false, RTPStatus: false });
  };

  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      case 'deleted':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="animate-spin inline-block h-8 w-8 border-4 border-cyan-500 border-t-transparent rounded-full mb-4"></div>
            <p>Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="text-white p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
              <div>
                <h1 className="text-4xl font-black bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
                  User Management
                </h1>
                <p className="text-gray-400">Manage user accounts, API quotas, and access</p>
              </div>
              <button
                onClick={() => {
                  resetAddUserForm();
                  setAddUserModal(true);
                }}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-semibold transition-all hover:shadow-lg hover:shadow-cyan-500/50"
              >
                <Plus size={20} />
                Add New User
              </button>
            </div>

            {/* Search Bar */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-4 mb-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search size={20} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search by name, registration number, or email..."
                  className="pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-700 rounded-lg w-full text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">User Info</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Email</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">API Usage</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">Premium Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">RTP Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">Passkey</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-4 text-center text-xs font-bold text-gray-300 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-8 text-gray-400">
                          <AlertCircle size={32} className="mx-auto mb-2 opacity-50" />
                          No users found
                        </td>
                      </tr>
                    ) : paginatedUsers.map(user => {
                      const usagePercentage = (user.apiUsed / user.apiLimit) * 100;
                      return (
                        <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          {/* User Info */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className="bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg p-2">
                                <User size={16} />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-white">{user.name}</div>
                                <div className="text-xs text-gray-400">{user.regNo}</div>
                              </div>
                            </div>
                          </td>

                          {/* Email */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            <div className="flex items-center gap-2">
                              <Mail size={14} className="text-gray-500" />
                              {user.email}
                            </div>
                          </td>

                          {/* API Usage */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="space-y-2">
                              <div className="w-32 bg-gray-700 rounded-full h-2 mx-auto">
                                <div 
                                  className={`h-2 rounded-full transition-all ${
                                  usagePercentage > 90 ? 'bg-red-500' : 
                                  usagePercentage > 70 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${Math.min(usagePercentage, 100)}%` }}
                                ></div>
                              </div>
                              <div className="text-xs text-gray-400 text-center">
                                {user.apiUsed.toLocaleString()} / {user.apiLimit.toLocaleString()}
                              </div>
                            </div>
                          </td>
                          
                          {/* Premium Status */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {/* --- FIX: Use user.premiumStatus, NOT newUser.premiumStatus --- */}
                            {user.premiumStatus === 'active' ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold gap-1 bg-gradient-to-r from-yellow-400 to-amber-500 text-gray-900">
                                <Check size={12} />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold gap-1 bg-gray-100 text-gray-800">
                                Inactive
                              </span>
                            )}
                          </td>

                          {/* RTP Status */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            {user.RTPStatus === 'active' ? (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold gap-1 bg-gradient-to-r from-green-400 to-teal-500 text-gray-900">
                                <Check size={12} />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold gap-1 bg-gray-100 text-gray-800">
                                Inactive
                              </span>
                            )}
                          </td>

                          {/* Passkey */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Key size={14} className="text-gray-500" />
                              <span className="font-mono text-sm text-gray-300">{user.passkey}</span>
                            </div>
                          </td>

                          {/* Status */}
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold gap-1 ${getStatusBadge(user.status)}`}>
                              {user.status === 'active' && <Check size={12} />}
                              {user.status === 'inactive' && <Lock size={12} />}
                              {user.status === 'deleted' && <X size={12} />}
                              {user.status && user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                            </span>
                          </td>

                          {/* Created Date */}
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Calendar size={14} />
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() => openIncreaseQuotaModal(user)}
                                className="p-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-lg transition-colors text-blue-300 hover:text-blue-200"
                                title="Increase Quota"
                              >
                                <RefreshCw size={16} />
                              </button>

                              <button
                                onClick={() => openApiKeyModal(user)}
                                className="p-2 bg-cyan-500/20 hover:bg-cyan-500/30 rounded-lg transition-colors text-cyan-300 hover:text-cyan-200"
                                title="View API Key"
                              >
                                <Key size={16} />
                              </button>

                              <button
                                onClick={() => openResetKeyModal(user)}
                                className="p-2 bg-purple-500/20 hover:bg-purple-500/30 rounded-lg transition-colors text-purple-300 hover:text-purple-200"
                                title="Reset API Key"
                              >
                                <Shield size={16} />
                              </button>

                              {user.status === 'active' ? (
                                <button
                                  onClick={() => openSuspendModal(user)}
                                  className="p-2 bg-yellow-500/20 hover:bg-yellow-500/30 rounded-lg transition-colors text-yellow-300 hover:text-yellow-200"
                                  title="Suspend User"
                                >
                                  <Lock size={16} />
                                </button>
                              ) : (
                                <button
                                  onClick={() => handleUnsuspendUser(user.id)}
                                  className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors text-green-300 hover:text-green-200"
                                  title="Unsuspend User"
                                >
                                  <Unlock size={16} />
                                </button>
                              )}

                              <button
                                onClick={() => openDeleteModal(user)}
                                className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors text-red-300 hover:text-red-200"
                                title="Delete User"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-white/10 flex justify-center">
                  <nav className="flex gap-2">
                    {[...Array(totalPages)].map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentPage(idx + 1)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          currentPage === idx + 1 
                            ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white'
                            : 'bg-white/5 hover:bg-white/10 border border-white/10 text-gray-300'
                        }`}
                      >
                        {idx + 1}
                      </button>
                    ))}
                  </nav>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==================== MODALS ==================== */}

      {/* Increase Quota Modal */}
      {increaseQuotaModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setIncreaseQuotaModal(false)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-blue-500/20 p-3 rounded-lg">
                <RefreshCw size={24} className="text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Increase API Quota</h2>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="text-sm text-gray-400">
                User: <span className="font-semibold text-white">{selectedUser?.name} ({selectedUser?.regNo})</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                  <div className="text-xs text-gray-400 mb-1">Current Limit</div>
                  <div className="text-lg font-bold text-cyan-400">{selectedUser?.apiLimit.toLocaleString()}</div>
                </div>
                <div className="bg-white/5 p-3 rounded-lg border border-white/10">
                  <div className="text-xs text-gray-400 mb-1">Remaining</div>
                  <div className={`text-lg font-bold ${(selectedUser?.apiLimit - selectedUser?.apiUsed) < 1000 ? 'text-red-400' : 'text-green-400'}`}>
                    {(selectedUser?.apiLimit - selectedUser?.apiUsed)?.toLocaleString()}
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Amount to Increase</label>
                <input
                  type="number"
                  min="1"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  value={increaseAmount}
                  onChange={(e) => setIncreaseAmount(e.target.value)}
                  placeholder="Enter number of API calls"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setIncreaseQuotaModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleIncreaseQuota}
                className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-cyan-500/50 transition-all font-semibold"
              >
                Increase
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspend User Modal */}
      {suspendModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setSuspendModal(false)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-yellow-500/20 p-3 rounded-lg">
                <Lock size={24} className="text-yellow-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Suspend User</h2>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-sm text-yellow-200">
                  User <strong>{selectedUser?.name}</strong> will be suspended. They won't be able to use the API.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Reason for Suspension</label>
                <textarea
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
                  rows="3"
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Enter reason (optional)"
                />
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setSuspendModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspendUser}
                className="flex-1 px-4 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-700 text-white transition-all font-semibold"
              >
                Suspend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setDeleteModal(false)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-red-500/20 p-3 rounded-lg">
                <Trash2 size={24} className="text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Delete User</h2>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                <p className="text-sm text-red-200">
                  ⚠️ <strong>Warning:</strong> Deleting <strong>{selectedUser?.name}</strong> will permanently remove their account and all associated data. This action cannot be undone.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                className="flex-1 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset API Key Modal */}
      {resetKeyModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setResetKeyModal(false)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-purple-500/20 p-3 rounded-lg">
                <Shield size={24} className="text-purple-400" />
              </div>
              <h2 className="text-xl font-bold text-white">Reset API Key</h2>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                <p className="text-sm text-purple-200">
                  Generate a new API key for <strong>{selectedUser?.name}</strong>. The old key will be invalidated.
                </p>
              </div>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setResetKeyModal(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleResetApiKey}
                className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:shadow-lg hover:shadow-purple-500/50 text-white transition-all font-semibold"
              >
                Reset Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add User Modal */}
        {addUserModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setAddUserModal(false)}>
            <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-green-500/20 p-3 rounded-lg">
              <User size={24} className="text-green-400" />
            </div>
            <h2 className="text-xl font-bold text-white">Add New User</h2>
          </div>
          
          <div className="space-y-3 mb-6">
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Full Name</label>
              <input
            type="text"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
            value={newUser.name}
            onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
            placeholder="Enter user name"
            autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Registration Number</label>
              <input
            type="text"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
            value={newUser.regNo}
            onChange={(e) => setNewUser({ ...newUser, regNo: e.target.value })}
            placeholder="Enter registration number"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-white mb-2">Email</label>
              <input
            type="email"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
            value={newUser.email}
            onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
            placeholder="Enter email address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Initial API Limit</label>
              <input
            type="number"
            min="1"
            className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-gray-500 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-all"
            value={newUser.apiLimit}
            onChange={(e) => setNewUser({ ...newUser, apiLimit: parseInt(e.target.value) || 50 })}
            placeholder="Default: 50"
              />
            </div>
              
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg p-2">
                <Shield size={18} className="text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Premium Access</div>
                <div className="text-xs text-gray-400">Enable premium features for this user</div>
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={newUser.premiumTaken || false}
                onChange={(e) => setNewUser({ ...newUser, premiumTaken: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-yellow-500 peer-checked:to-orange-500"></div>
            </div>
              </label>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-500 to-orange-500 rounded-lg p-2">
                <MonitorUp size={18} className="text-white" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white">Remote Access</div>
                <div className="text-xs text-gray-400">Enable remote access for this user</div>
              </div>
            </div>
            <div className="relative">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={newUser.RTPStatus || false}
                onChange={(e) => setNewUser({ ...newUser, RTPStatus: e.target.checked })}
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-cyan-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-gradient-to-r peer-checked:from-yellow-500 peer-checked:to-orange-500"></div>
            </div>
              </label>
            </div>
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => setAddUserModal(false)}
              className="flex-1 px-4 py-2 rounded-lg border border-white/10 text-white hover:bg-white/5 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleAddUser}
              className="flex-1 px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-lg hover:shadow-green-500/50 text-white transition-all font-semibold"
            >
              Create User
            </button>
          </div>
            </div>
          </div>
        )}

        {/* API Key Modal */}
      {apiKeyModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setApiKeyModal(false)}>
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-white/10 rounded-xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-cyan-500/20 p-3 rounded-lg">
                <Key size={24} className="text-cyan-400" />
              </div>
              <h2 className="text-xl font-bold text-white">API Key</h2>
            </div>
            
            <div className="space-y-4 mb-6">
              <div className="text-sm text-gray-400">
                User: <span className="font-semibold text-white">{selectedUser?.name} ({selectedUser?.regNo})</span>
              </div>
              
              <div className="bg-white/5 border border-white/10 p-4 rounded-lg relative">
                <div className="text-xs text-gray-400 mb-2">API Key</div>
                <div className="font-mono text-sm break-all pr-10 text-cyan-300">
                  {showApiKey ? newApiKey || selectedUser?.apiKey : '••••••••••••••••••••••••••••••••'}
                </div>
                <button 
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-cyan-400 transition-colors"
                >
                  {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>

              <button
                onClick={() => {
                  navigator.clipboard.writeText(newApiKey || selectedUser?.apiKey);
                  alert('API key copied to clipboard');
                }}
                className="w-full px-4 py-2 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-lg transition-all text-sm font-semibold"
              >
                Copy to Clipboard
              </button>
              
              <div className="text-xs text-gray-400">
                <p>⚠️ Keep this key secure. Do not share it publicly or commit it to version control.</p>
              </div>
            </div>
            
            <button
              onClick={() => setApiKeyModal(false)}
              className="w-full px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:shadow-lg hover:shadow-cyan-500/50 transition-all font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}